/**
 * HR-first-review action bar — Forward / Request document / Reject.
 *
 * Rendered wherever HR acts on a `pending_hr_review` leave: the HR Doc Review
 * queue's ⋮ menu opens the leave detail modal, and this bar lives inside that
 * modal so HR can decide without leaving the page. Backed by
 * /api/v1/hr/leave-reviews.
 *
 * `onDone` is called after a successful action so the host can refresh the
 * detail / close the modal; the shared ['hr-review-queue'] queries are
 * invalidated here so the queue + Approvals badge update too.
 */
import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { ApiError } from '../../api/client'
import {
  forwardLeaveReview,
  rejectLeaveReview,
  requestLeaveDocument,
} from '../../api/hr-leave-reviews'
import { Button } from '../primitives/Button'
import { Modal } from '../primitives/Modal'
import { useToast } from '../primitives/Toast'

interface HrReviewActionsProps {
  leaveId: string
  employeeName: string
  /** Called after a successful action (refresh the detail / close the modal). */
  onDone: () => void | Promise<void>
}

type ActionKind = 'reject' | 'request'

const ACTION_COPY: Record<
  ActionKind,
  { title: string; label: string; placeholder: string; confirm: string; variant: 'destructive' | 'primary' }
> = {
  reject: {
    title: 'Reject this request',
    label: 'Reason (shown to the employee)',
    placeholder: 'e.g. The attached certificate is illegible.',
    confirm: 'Reject',
    variant: 'destructive',
  },
  request: {
    title: 'Request a document',
    label: 'What does the employee need to attach?',
    placeholder: 'e.g. Please upload a clear photo of your MC.',
    confirm: 'Send request',
    variant: 'primary',
  },
}

function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message
  if (err instanceof Error) return err.message
  return 'Something went wrong.'
}

export function HrReviewActions({ leaveId, employeeName, onDone }: HrReviewActionsProps): JSX.Element {
  const { notify } = useToast()
  const queryClient = useQueryClient()
  const [action, setAction] = useState<ActionKind | null>(null)
  const [text, setText] = useState('')
  const [formError, setFormError] = useState<string | null>(null)

  function closeModal(): void {
    setAction(null)
    setText('')
    setFormError(null)
  }

  async function afterAction(): Promise<void> {
    await queryClient.invalidateQueries({ queryKey: ['hr-review-queue'] })
    await onDone()
  }

  const forwardMutation = useMutation({
    mutationFn: () => forwardLeaveReview(leaveId),
    onSuccess: async () => {
      notify(`Forwarded ${employeeName}'s request to their manager.`, 'success')
      await afterAction()
    },
    onError: (err) => notify(`Forward failed — ${errorMessage(err)}`, 'error'),
  })

  const textMutation = useMutation({
    mutationFn: ({ kind, value }: { kind: ActionKind; value: string }) =>
      kind === 'reject' ? rejectLeaveReview(leaveId, value) : requestLeaveDocument(leaveId, value),
    onSuccess: async (_leave, { kind }) => {
      notify(
        kind === 'reject' ? `Rejected ${employeeName}'s request.` : `Asked ${employeeName} for a document.`,
        'success',
      )
      closeModal()
      await afterAction()
    },
    onError: (err) => setFormError(errorMessage(err)),
  })

  function submitModal(): void {
    if (action == null) return
    const value = text.trim()
    if (value.length === 0) {
      setFormError('This field is required.')
      return
    }
    setFormError(null)
    textMutation.mutate({ kind: action, value })
  }

  const busy = forwardMutation.isPending

  return (
    <>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
        <Button
          variant="soft"
          onClick={() => {
            setAction('request')
            setText('')
            setFormError(null)
          }}
          data-testid="hr-review-modal-request-doc"
        >
          Request document
        </Button>
        <Button
          variant="destructive"
          onClick={() => {
            setAction('reject')
            setText('')
            setFormError(null)
          }}
          data-testid="hr-review-modal-reject"
        >
          Reject
        </Button>
        <Button
          variant="primary"
          loading={busy}
          onClick={() => forwardMutation.mutate()}
          data-testid="hr-review-modal-forward"
        >
          Forward to approver
        </Button>
      </div>

      <Modal
        open={action != null}
        onClose={closeModal}
        title={action ? ACTION_COPY[action].title : ''}
        subtitle={employeeName}
        footer={
          action ? (
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <Button variant="tertiary" onClick={closeModal} disabled={textMutation.isPending}>
                Cancel
              </Button>
              <Button
                variant={ACTION_COPY[action].variant}
                onClick={submitModal}
                loading={textMutation.isPending}
              >
                {ACTION_COPY[action].confirm}
              </Button>
            </div>
          ) : undefined
        }
      >
        {action && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>
              {ACTION_COPY[action].label}
            </label>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={ACTION_COPY[action].placeholder}
              rows={3}
              maxLength={1000}
              autoFocus
              style={{
                width: '100%',
                padding: 10,
                fontSize: 13,
                borderRadius: 8,
                border: '1px solid var(--border)',
                background: 'var(--surface)',
                color: 'var(--text)',
                resize: 'vertical',
              }}
            />
            {formError && <span style={{ fontSize: 12, color: 'var(--danger)' }}>{formError}</span>}
          </div>
        )}
      </Modal>
    </>
  )
}
